/**
 * Load Controls Component
 * Main processing controls for the Load application
 * Based on Load Vue LoadControls.vue
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Stack,
  Box,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  PlayArrow as ProcessIcon,
  Settings as SettingsIcon,
  ExitToApp as RemoveIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigationStore } from '@/stores/navigation-store';
import { useCompanyStore } from '@/stores/company-store';
import { apiClient } from '@/lib/api-client';
import { useSnackbarStore } from '@certara/common-react-components';
import { RemoveFromDbDialog } from '@/components/Dialogs';
import { useConfiguration } from '@/hooks/use-configuration';

interface LoadControlsProps {
  disabled?: boolean;
}

export function LoadControls({ disabled = false }: LoadControlsProps) {
  const { t } = useTranslation('load');
  const { selectedItems, currentPath, setSelectedItems } = useNavigationStore();
  const { activeCompany } = useCompanyStore();
  const queryClient = useQueryClient();
  const [processMenuOpen, setProcessMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const { addSnackbar } = useSnackbarStore();
  
  // Fetch company configuration to get default processing options
  const { data: companyConfig } = useConfiguration(activeCompany?.Id);

  // Process settings state - initialized with defaults, then updated from company config
  const [processOptions, setProcessOptions] = useState({
    ValidateXMLStructure: true,
    ValidatePdfs: false,
    ValidateChecksums: false,
  });

  // Initialize process options from company configuration defaults (once only)
  const initializedFromConfig = useRef(false);
  useEffect(() => {
    if (companyConfig && !initializedFromConfig.current) {
      initializedFromConfig.current = true;
      setProcessOptions(prev => ({
        ...prev,
        ValidatePdfs: companyConfig.ValidatePdfs,
        ValidateChecksums: companyConfig.ValidateChecksums,
      }));
    }
  }, [companyConfig]);

  const [selectedFormat, setSelectedFormat] = useState<'eCTD' | 'NeeS Application' | 'NeeS Sequence'>('eCTD');

  // Track timeout IDs for cleanup
  const refreshTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      refreshTimeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const selectedFolders = useMemo(
    () => selectedItems.filter((item) => item.IsFolder),
    [selectedItems],
  );
  const hasFoldersSelected = selectedFolders.length >= 1;

  const anyProcessOptionsChecked = Object.values(processOptions).some(Boolean);
  const canProcess = hasFoldersSelected && anyProcessOptionsChecked && !disabled && activeCompany;

  // "Remove from DB" is enabled when all selected folders are loaded (IsLoadedApp or IsLoadedSub)
  const loadedFolders = useMemo(
    () => selectedFolders.filter((f) => f.IsLoadedApp || f.IsLoadedSub),
    [selectedFolders],
  );
  const canRemove = hasFoldersSelected && loadedFolders.length === selectedFolders.length && !disabled && activeCompany;

  const getListOfCheckedOptions = () => {
    const checkedOptions = Object.entries(processOptions)
      .filter(([, checked]) => checked)
      .map(([key]) => {
        switch (key) {
          case 'ValidateXMLStructure': return t('controls.optionXmlStructure');
          case 'ValidatePdfs': return t('controls.optionProcessPdfs');
          case 'ValidateChecksums': return t('controls.optionVerifyChecksums');
          default: return key;
        }
      });

    return checkedOptions.length > 0 ? checkedOptions.join(', ') : t('controls.optionNone');
  };

  // Construct folderPath like Vue does (current path without the folder name)
  const constructFolderPath = (): string => {
    if (!currentPath) return '';
    // The folderPath is the parent path (currentPath in the navigation store)
    return currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  };

  const handleProcessClick = async () => {
    if (!canProcess || !activeCompany) return;
    if (selectedFolders.length === 0) return;

    const folderPath = constructFolderPath();
    const succeeded: string[] = [];
    const failed: string[] = [];

    // Process each selected folder, continuing on error so remaining folders still get queued
    for (const folder of selectedFolders) {
      try {
        const requestBody = {
          Name: folder.Name,
          Path: folder.Path,
          IsFolder: folder.IsFolder,
          IsLoadedApp: folder.IsLoadedApp,
          IsLoadedSub: folder.IsLoadedSub,
          IsSub: folder.IsSub,
          AppId: folder.AppId,
          SubId: folder.SubId,
          SourceAppId: folder.SourceAppId,
          SourceAppType: folder.SourceAppType,
          SourceSubId: folder.SourceSubId,
          SourceSubType: folder.SourceSubType,
          Backbone: folder.Backbone,
          IsNeeS: folder.IsNeeS,
          FolderPath: folderPath,
          SelectedFormat: selectedFormat,
          ProcessXml: processOptions.ValidateXMLStructure,
          ProcessPdfs: processOptions.ValidatePdfs,
          CalculateChecksums: processOptions.ValidateChecksums,
        };

        await apiClient.post(
          `api/Loading/LoadFolder?companyId=${activeCompany.Id}`,
          requestBody
        );
        succeeded.push(folder.Name);
      } catch {
        failed.push(folder.Name);
      }
    }

    if (succeeded.length > 0) {
      const message = succeeded.length === 1
        ? t('controls.snackbarProcessStartedSingle', { name: succeeded[0] })
        : t('controls.snackbarProcessStartedMultiple', { count: succeeded.length });
      addSnackbar(message, 'success');
    }

    if (failed.length > 0) {
      addSnackbar(
        t('controls.snackbarProcessFailed', { count: failed.length, names: failed.join(', ') }),
        'error',
        undefined,
        true
      );
    }

    // Refresh grid and queue data immediately so the Activity indicator reflects the new job
    queryClient.invalidateQueries({ queryKey: ['gridData', activeCompany.Id, currentPath] });
    queryClient.invalidateQueries({ queryKey: ['queue'] });

    // Clear any existing refresh timeouts before scheduling new ones
    refreshTimeoutsRef.current.forEach(clearTimeout);
    refreshTimeoutsRef.current = [];

    // Schedule multiple refreshes to catch when processing completes
    [2000, 5000, 10000, 15000].forEach((delay) => {
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['gridData', activeCompany.Id, currentPath] });
      }, delay);
      refreshTimeoutsRef.current.push(timeoutId);
    });
  };

  const handleRemoveClick = () => {
    if (!canRemove) return;
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!activeCompany || loadedFolders.length === 0) return;

    setIsRemoving(true);
    try {
      for (const folder of loadedFolders) {
        await apiClient.post(
          `api/Loading/UnloadFolder?companyId=${activeCompany.Id}`,
          folder
        );
      }

      const message = loadedFolders.length === 1
        ? t('controls.snackbarRemovedSingle', { name: loadedFolders[0].Name })
        : t('controls.snackbarRemovedMultiple', { count: loadedFolders.length });
      addSnackbar(message, 'success');

      // Clear selection and refresh grid data to update IsLoadedApp/IsLoadedSub flags
      setSelectedItems([]);
      queryClient.invalidateQueries({ queryKey: ['gridData', activeCompany.Id, currentPath] });
    } catch (error) {
      addSnackbar(
        t('controls.snackbarRemoveFailed'),
        'error',
        error instanceof Error ? error.message : String(error),
        true
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setProcessMenuOpen(true);
  };

  const handleMenuClose = () => {
    setProcessMenuOpen(false);
    setAnchorEl(null);
  };

  const handleOptionChange = (option: keyof typeof processOptions) => {
    // Special handling for XML validation - only enable when folder is selected
    if (option === 'ValidateXMLStructure' && !hasFoldersSelected) {
      return;
    }

    setProcessOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const getTooltipText = () => {
    if (!hasFoldersSelected) {
      return t('controls.tooltipSelectFolders');
    }
    const folderCount = selectedFolders.length;
    const folderLabel = folderCount === 1 ? selectedFolders[0].Name : t('controls.folderCount', { count: folderCount });
    return t('controls.tooltipProcessDetail', { folderLabel, format: selectedFormat, options: getListOfCheckedOptions() });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="load-controls-container">
      <Tooltip title={getTooltipText()}>
        <Box sx={{ display: 'inline-block' }}>
          <Button
            data-testid="load-controls-process-button"
            variant="text"
            startIcon={<ProcessIcon />}
            onClick={handleProcessClick}
            disabled={!canProcess}
            sx={{
              color: 'white',
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
              px: 2,
              py: 1.5,
            }}
          >
            {t('controls.process')}
          </Button>
        </Box>
      </Tooltip>

      <Divider
        orientation="vertical"
        sx={{
          mx: 1,
          height: 32,
          borderColor: canProcess ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)',
        }}
      />

      <Tooltip title={t('controls.processSettings')}>
        <Box sx={{ display: 'inline-block' }}>
          <Button
            data-testid="load-controls-settings-button"
            variant="text"
            onClick={handleSettingsClick}
            disabled={!hasFoldersSelected || disabled}
            sx={{
              color: 'white',
              minWidth: 'auto',
              px: 1.5,
              py: 1.5,
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <SettingsIcon />
          </Button>
        </Box>
      </Tooltip>

      <Menu
        data-testid="load-controls-settings-menu"
        anchorEl={anchorEl}
        open={processMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 300, p: 2 }
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }} data-testid="load-controls-settings-menu-title">
          {t('controls.processingOptions')}
        </Typography>

        {/* Format Selection */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t('controls.formatLabel')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} data-testid="load-controls-format-selector">
          {(['eCTD', 'NeeS Application', 'NeeS Sequence'] as const).map((format) => (
            <Button
              key={format}
              data-testid={`load-controls-format-${format.replace(/\s+/g, '-').toLowerCase()}`}
              variant={selectedFormat === format ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedFormat(format)}
            >
              {format}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Processing Options */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t('controls.optionsLabel')}
        </Typography>

        <Stack spacing={1} data-testid="load-controls-options-list">
          <FormControlLabel
            control={
              <Checkbox
                data-testid="load-controls-option-validate-xml"
                checked={processOptions.ValidateXMLStructure}
                onChange={() => handleOptionChange('ValidateXMLStructure')}
                size="small"
                disabled={!hasFoldersSelected}
              />
            }
            label={t('controls.validateXml')}
          />
          <FormControlLabel
            control={
              <Checkbox
                data-testid="load-controls-option-process-pdfs"
                checked={processOptions.ValidatePdfs}
                onChange={() => handleOptionChange('ValidatePdfs')}
                size="small"
              />
            }
            label={t('controls.processPdfs')}
          />
          <FormControlLabel
            control={
              <Checkbox
                data-testid="load-controls-option-verify-checksums"
                checked={processOptions.ValidateChecksums}
                onChange={() => handleOptionChange('ValidateChecksums')}
                size="small"
              />
            }
            label={t('controls.verifyChecksums')}
          />
        </Stack>

        {!anyProcessOptionsChecked && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="load-controls-options-warning">
            {t('controls.optionsWarning')}
          </Alert>
        )}
      </Menu>

      {/* Unload Button (removes loaded data from database) */}
      <Tooltip title={!canRemove ? t('controls.tooltipSelectLoadedFolders') : t('controls.tooltipUnloadDetail', { folderLabel: loadedFolders.length === 1 ? loadedFolders[0].Name : t('controls.folderCount', { count: loadedFolders.length }) })}>
        <Box sx={{ display: 'inline-block', ml: 2 }}>
          <Button
            data-testid="load-controls-remove-button"
            variant="text"
            startIcon={<RemoveIcon />}
            onClick={handleRemoveClick}
            disabled={!canRemove}
            aria-label={t('controls.unloadAriaLabel')}
            sx={{
              color: 'white',
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
              px: 2,
              py: 1.5,
            }}
          >
            {t('controls.unload')}
          </Button>
        </Box>
      </Tooltip>

      {/* Remove from DB Confirmation Dialog */}
      <RemoveFromDbDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={handleRemoveConfirm}
        folders={loadedFolders}
        isLoading={isRemoving}
      />
    </Box>
  );
}